/** Write-ahead recovery for the credential stores that span native files, IDB and localStorage. */
import { db } from '../platform/webStorage'
import { secureStorage } from './secureStorage'
import { getPlatformBridge } from '../platform/platformBridge'
import { keyManager } from './keyManager'
import { throwIfAborted } from '../sync/requestDeadline'

function journal() {
  const bridge = getPlatformBridge()
  return {
    read: () => bridge?.readCredentialJournal ? bridge.readCredentialJournal() : db.credentialTransactions.get('pending'),
    write: async (data: any) => { if (bridge?.writeCredentialJournal) await bridge.writeCredentialJournal(data); else await db.credentialTransactions.put(data) },
    delete: async () => { if (bridge?.deleteCredentialJournal) await bridge.deleteCredentialJournal(); else await db.credentialTransactions.delete('pending') },
  }
}

let queue: Promise<unknown> = Promise.resolve()
function serialize<T>(operation: () => Promise<T>): Promise<T> {
  const task = queue.catch(() => {}).then(async (): Promise<T> => {
    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
      return await navigator.locks.request('superelectric-ink-credentials', operation)
    }
    return operation()
  })
  queue = task
  return task
}

async function recover(): Promise<void> {
  const saved = await journal().read()
  if (!saved) return
  const bridge = getPlatformBridge()
  if (saved.certificate) await bridge.storeActivationCertificate(saved.certificate)
  else await bridge.deleteActivationCertificate()
  if (saved.keys) await keyManager.persistPreparedKeyStore(saved.keys)
  else {
    const result = await bridge.deleteKeyStore()
    if (!result?.success) throw new Error('Unable to recover the previous encryption keys')
  }
  await bridge.restoreLocalAccountId?.(saved.owner ?? null)
  secureStorage.restore(saved.metadata)
  // Keep the journal until every part of the previous state has been restored.
  await journal().delete()
}

export function recoverCredentialTransaction(): Promise<void> { return serialize(recover) }

export function commitCredentials(operation: () => Promise<void>, signal?: AbortSignal, afterCommit?: () => Promise<void>): Promise<void> {
  return serialize(async () => {
    await recover()
    throwIfAborted(signal)
    await keyManager.pausePersistence()
    try {
      const bridge = getPlatformBridge()
      const metadata = secureStorage.snapshot()
      const keys = await bridge.readKeyStore()
      const owner = await bridge.readLocalAccountId?.() ?? null
      let certificate = await bridge.readActivationCertificate?.() ?? null
      if (!certificate && metadata.superelectric_ink_activation_certificate_v1) {
        try { certificate = JSON.parse(metadata.superelectric_ink_activation_certificate_v1) } catch { /* An unreadable old certificate cannot grant local access. */ }
      }
      await journal().write({ id: 'pending', metadata, keys, certificate, owner })
      try {
        throwIfAborted(signal)
        await operation()
        throwIfAborted(signal)
        await journal().delete()
      } catch (error) {
        try { await recover() } catch {
          throw new Error('Credential storage failed and recovery is incomplete. Free storage space and restart the app to recover your previous local access.')
        }
        throw error
      }
      await afterCommit?.()
    } finally { keyManager.resumePersistence() }
  })
}
